import { useTranslation } from '@jobcost-monorepo/i18n';
import { ITimesheet } from '@jobcost-monorepo/crud';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { floatToHours, ITimesheetsByResourceByDate } from '../helpers';
import dayjs from '@jobcost-monorepo/dayjs-config';

interface Props {
  dataList: ITimesheetsByResourceByDate[];
  startDate?: string;
  endDate?: string;
}

const TimesheetSinglePdf = ({ dataList, startDate, endDate }: Props) => {
  if (dataList.length === 0) {
    return <Document></Document>;
  }
  const { t } = useTranslation('timesheets');
  const data = dataList[0];
  const start = dayjs.utc(startDate);
  const year = dayjs.utc(startDate).year();
  const month = dayjs
    .utc(startDate)
    .format('MMMM')
    .toString()
    .toLocaleUpperCase();
  const end = dayjs.utc(endDate);
  const daysInMonth = end.diff(start, 'day') + 1;
  const fullName = data.resource.name.split(' ').join('_').toLocaleUpperCase();
  const defaultName = `REPORT_${month}_${year}_${fullName}.pdf`;

  const daysArray = Array.from({ length: daysInMonth }, (_, i) =>
    start.add(i, 'day'),
  );

  const getTimesheetsForDate = (date: string): ITimesheet[] => {
    const group = data.groupedByDate.find(
      (g) => dayjs.utc(g.operationDate).format('YYYY-MM-DD') === date,
    );
    let workedHours = 0;
    if (group && group?.timesheets.length >= 2) {
      group.timesheets.map((timesheet) => {
        if (timesheet.workedHours) {
          workedHours = workedHours + timesheet.workedHours;
        }
      });
    } else {
      if (group?.timesheets[0].workedHours) {
        workedHours = group?.timesheets[0].workedHours;
      }
    }
    return group ? group.timesheets : [];
  };

  const getTotalHoursByMarkType = (markType: string): string => {
    const totalHours = data.groupedByDate.reduce((total, group) => {
      return (
        total +
        group.timesheets.reduce((sum, timesheet) => {
          return (
            sum +
            (timesheet.markType === markType ? timesheet.workedHours ?? 0 : 0)
          );
        }, 0)
      );
    }, 0);

    return floatToHours(totalHours);
  };

  const getTotalHours = (markType: string, index: number) => {
    if (index === 0) {
      return `${t(`enum.markType.${markType}`)} ${getTotalHoursByMarkType(markType)} `;
    } else {
      return ` - ${t(`enum.markType.${markType}`)} ${getTotalHoursByMarkType(markType)}`;
    }
  };

  const getHours = (timesheets: ITimesheet[], markType: string) => {
    if (markType === 'WorkedHours') {
      return `${getTodayHoursByMarkType(timesheets, markType)}h`;
    }
    return `${getTodayHoursByMarkType(timesheets, markType)}h ${t(`enum.markTypeShort.${markType}`).toLocaleUpperCase()}`;
  };

  const getTodayHoursByMarkType = (
    timesheets: ITimesheet[],
    markType: string,
  ) => {
    return timesheets.reduce((totalHours, timesheet) => {
      return (
        totalHours +
        (timesheet.markType === markType ? timesheet.workedHours ?? 0 : 0)
      );
    }, 0);
  };

  const getLine = (timesheets: ITimesheet[]) => {
    const line = markTypes.map((markType) => {
      if (getTodayHoursByMarkType(timesheets, markType) !== 0) {
        return `${getHours(timesheets, markType)}`;
      } else {
        return '';
      }
    });

    return line.filter((value) => value !== '').join(' - ');
  };

  let markTypes = Array.from(
    new Set(
      data.groupedByDate.flatMap((group) =>
        group.timesheets.map((ts) => ts.markType),
      ),
    ),
  );

  if (markTypes.includes('WorkedHours')) {
    markTypes = [
      'WorkedHours',
      ...markTypes.filter((markType) => markType !== 'WorkedHours'),
    ];
  }

  const styles = StyleSheet.create({
    page: {
      padding: 5,
      fontSize: 12,
    },
    section: {
      borderLeftWidth: '1px',
      borderTopWidth: '1px',
      borderColor: 'black',
      width: '540rem',
    },
    table: {
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      borderBottomWidth: '1px',
      borderColor: 'black',
      fontWeight: 600,
    },
    headerTitle: {
      width: '100%',
      justifyContent: 'center',
      borderRightWidth: '1px',
      alignItems: 'center',
      paddingVertical: 5,
    },
    headerTitleDate: {
      width: '150rem',
      justifyContent: 'center',
      borderRightWidth: '1px',
      alignItems: 'center',
      paddingVertical: 5,
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
      paddingTop: 8,
    },
    title: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      paddingVertical: 8,
    },
    tablePositioning: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
  });

  return (
    <Document title={defaultName}>
      <Page size="A4" orientation="portrait" style={styles.page}>
        <View style={styles.title}>
          <Text>
            {month} {year}
          </Text>
        </View>
        <View style={styles.tablePositioning}>
          <View style={styles.section}>
            <View style={styles.header} wrap={false}>
              <View style={styles.headerTitleDate}>
                <Text>{t('grid.header.date')}</Text>
              </View>

              <View style={styles.headerTitle}>
                <Text>{data.resource.name}</Text>
              </View>
            </View>

            <View style={styles.table}>
              {daysArray.map((day) => {
                const dateKey = day.format('YYYY-MM-DD');
                const timesheets = getTimesheetsForDate(dateKey);

                return (
                  <View style={styles.header} key={dateKey} wrap={false}>
                    <View style={styles.headerTitleDate}>
                      <Text>
                        {day.locale('it').format('DD/MM/YYYY').toUpperCase()}
                      </Text>
                    </View>
                    <View style={styles.headerTitle}>
                      <Text>
                        {timesheets.length > 0 && (
                          <Text>{getLine(timesheets)}</Text>
                        )}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          {markTypes.map((markType, index) => {
            return <Text key={markType}>{getTotalHours(markType, index)}</Text>;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default TimesheetSinglePdf;
